import React, { useState, useMemo } from 'react';
import AdminSidebar from './AdminSidebar';
import { usePermission } from '../hooks/usePermission';
import { PERMISSIONS } from '../lib/permissions';

interface AdminShellProps {
  children: React.ReactNode;
}

const BentoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <rect x="3" y="3" width="4" height="4" rx="1" />
    <rect x="10" y="3" width="4" height="4" rx="1" />
    <rect x="17" y="3" width="4" height="4" rx="1" />
    <rect x="3" y="10" width="4" height="4" rx="1" />
    <rect x="10" y="10" width="4" height="4" rx="1" />
    <rect x="17" y="10" width="4" height="4" rx="1" />
    <rect x="3" y="17" width="4" height="4" rx="1" />
    <rect x="10" y="17" width="4" height="4" rx="1" />
    <rect x="17" y="17" width="4" height="4" rx="1" />
  </svg>
);

const GridIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 3.75h6.5v6.5h-6.5zm10 0h6.5v6.5h-6.5zm-10 10h6.5v6.5h-6.5zm10 0h6.5v6.5h-6.5z"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
    />
  </svg>
);

const UserGroupIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
    />
  </svg>
);

const CubeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
    />
  </svg>
);

const TagIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 6h.008v.008H6V6z"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
    />
  </svg>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const ALL_NAV_ITEMS = [
  {
    id: 'overview',
    label: 'Dashboard',
    icon: <GridIcon />,
    href: '/console',
    category: 'workspace' as const,
  },
  {
    id: 'organizations',
    label: 'Organizations',
    icon: <BuildingIcon />,
    href: '/organizations',
    category: 'workspace' as const,
    actionId: PERMISSIONS.ACTION_ORG_VIEW,
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    icon: <MailIcon />,
    href: '/subscriptions',
    category: 'workspace' as const,
    actionId: PERMISSIONS.ACTION_SUBSCRIPTION_VIEW,
  },
  {
    id: 'roles',
    label: 'Roles',
    icon: <ShieldIcon />,
    href: '/admin/roles',
    category: 'access' as const,
    actionId: PERMISSIONS.ACTION_ROLE_VIEW,
  },
  {
    id: 'groups',
    label: 'Groups',
    icon: <UserGroupIcon />,
    href: '/admin/groups',
    category: 'access' as const,
    actionId: PERMISSIONS.ACTION_GROUP_VIEW,
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: <CubeIcon />,
    href: '/admin/resources',
    category: 'access' as const,
    actionId: PERMISSIONS.ACTION_ORG_VIEW_ALL,
  },
  {
    id: 'plans',
    label: 'Plans',
    icon: <TagIcon />,
    href: '/admin/plans',
    category: 'billing' as const,
    actionId: PERMISSIONS.ACTION_SUBSCRIPTION_MANAGE,
  },
  {
    id: 'org-plans',
    label: 'Org Plans',
    icon: <TagIcon />,
    href: '/org-plans',
    category: 'billing' as const,
    actionId: PERMISSIONS.ACTION_SUBSCRIPTION_MANAGE,
  },
  // { id: 'shops', label: 'Shop Reviews', icon: <StoreIcon />, href: '/admin/shops/verification', badge: 'Review', badgeColor: 'warning', category: 'operations' as const },
  {
    id: 'profile',
    label: 'My Account',
    icon: <UserIcon />,
    href: '/profile',
    category: 'account' as const,
  },
];

const AdminShell: React.FC<AdminShellProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { canByActionId, isLoading: permissionsLoading } = usePermission();

  const adminNavItems = useMemo(
    () =>
      ALL_NAV_ITEMS.filter(
        item =>
          !item.actionId || permissionsLoading || canByActionId(item.actionId)
      ),
    [canByActionId, permissionsLoading]
  );

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      {/* Mobile Top Bar */}
      <div
        className="lg:hidden flex items-center justify-between px-4 h-16 border-b sticky top-0 z-30"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border)',
        }}
      >
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-xl text-[var(--accent)] transition-all active:scale-90 hover:bg-[var(--accent)]/10"
          aria-label="Toggle Menu"
        >
          <BentoIcon />
        </button>

        <div className="flex items-center gap-2">
          <span className="font-black text-sm tracking-[0.2em] uppercase opacity-40">
            SudoSys
          </span>
          <div className="w-8 h-8 rounded-lg bg-[var(--accent)] flex items-center justify-center text-white font-bold shadow-lg shadow-[var(--accent)]/20">
            S
          </div>
        </div>
      </div>

      <AdminSidebar
        items={adminNavItems}
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      />
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
};

export default AdminShell;
